import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import BuildIcon from '@mui/icons-material/Build';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import { format } from 'date-fns';
import api from '../services/api';
import { ActivityLogEntry } from '../types/order';

const FILTERS = [
  { label: 'All', value: '' },
  { label: 'Notes', value: 'note' },
  { label: 'Provisioning', value: 'provisioning' },
  { label: 'Status', value: 'status_change' },
  { label: 'Emails', value: 'email' },
];

const TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  note:          { label: 'Note',         icon: <StickyNote2Icon sx={{ fontSize: 14 }} />, color: 'info.main' },
  provisioning:  { label: 'Provisioning', icon: <BuildIcon sx={{ fontSize: 14 }} />,       color: 'secondary.main' },
  status_change: { label: 'Status',       icon: <SwapHorizIcon sx={{ fontSize: 14 }} />,   color: 'warning.main' },
  email:         { label: 'Email',        icon: <EmailIcon sx={{ fontSize: 14 }} />,        color: 'success.main' },
  approval_link: { label: 'Link',         icon: <LinkIcon sx={{ fontSize: 14 }} />,         color: 'primary.main' },
};

function TypeChip({ type }: { type: string }) {
  const cfg = TYPE_CONFIG[type];
  const color = cfg?.color ?? 'text.secondary';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ color, display: 'flex' }}>{cfg?.icon ?? <StickyNote2Icon sx={{ fontSize: 14 }} />}</Box>
      <Typography variant="caption" sx={{ color, fontWeight: 500, whiteSpace: 'nowrap' }}>
        {cfg?.label ?? type}
      </Typography>
    </Box>
  );
}

type Props = {
  orderId: string;
  refreshKey?: number;
};

function OrderActivityLog({ orderId, refreshKey }: Props) {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const types = activeFilter ? [activeFilter] : undefined;
      const data = await api.adminGetOrderActivityLog(orderId, types);
      setEntries(data.entries);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [orderId, activeFilter, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Activity Log</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {FILTERS.map((f) => (
            <Chip
              key={f.value}
              label={f.label}
              size="small"
              variant={activeFilter === f.value ? 'filled' : 'outlined'}
              color={activeFilter === f.value ? 'primary' : 'default'}
              onClick={() => setActiveFilter(f.value)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1e3a5c', '& .MuiTableCell-head': { color: '#ffffff', fontWeight: 600 } }}>
                <TableCell width={120}>Type</TableCell>
                <TableCell>Details</TableCell>
                <TableCell width={140}>By</TableCell>
                <TableCell width={150}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    <TypeChip type={entry.activity_type} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {entry.content || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {entry.performed_by_name ?? 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {format(new Date(entry.performed_at), 'dd MMM yyyy HH:mm')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default OrderActivityLog;
